import React, { useState, useEffect } from 'react';
import { admin } from '../../services/api';
import '../../styles/AdminUserManagement.css';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAuditDrawer, setShowAuditDrawer] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [auditUser, setAuditUser] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    credentials: '',
    role: 'pharmacist',
    is_active: true,
    default_pay_rate: 0,
    default_pay_method: 'per_medication',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await admin.getUsers();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      if (err.response?.status === 404) {
        setUsers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await admin.createUser(formData);
      alert('User created successfully!');
      setShowCreateModal(false);
      setFormData({
        email: '',
        password: '',
        full_name: '',
        credentials: '',
        role: 'pharmacist',
        default_pay_rate: 0,
        default_pay_method: 'per_medication',
      });
      fetchUsers();
    } catch (err) {
      console.error('Failed to create user:', err);
      alert('Create user failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const payRateCents = Math.round(parseFloat(formData.default_pay_rate) * 100);
      await admin.updateUser(editingUser.id, {
        full_name: formData.full_name,
        credentials: formData.credentials,
        role: formData.role,
        is_active: formData.is_active,
        default_pay_rate: payRateCents,
        default_pay_method: formData.default_pay_method,
      });
      alert('User updated successfully!');
      setShowEditModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      alert('Update user failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      await admin.deleteUser(deletingUser.id);
      alert(`User '${deletingUser.full_name}' has been deactivated and their pending reviews released.`);
      setShowDeleteModal(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Delete user failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleStatus = async (userId, currentStatus, userName) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    if (window.confirm(`${newStatus === 'inactive' ? 'Deactivate' : 'Activate'} ${userName}?`)) {
      try {
        await admin.toggleUserStatus(userId, newStatus);
        alert('User status updated!');
        fetchUsers();
      } catch (err) {
        console.error('Failed to toggle status:', err);
        alert('Status update failed: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleViewAudit = async (user) => {
    setAuditUser(user);
    setShowAuditDrawer(true);
    setLoadingAudit(true);
    setAuditLogs([]);
    try {
      const response = await admin.getUserAudit(user.id);
      setAuditLogs(response.data || []);
    } catch (err) {
      console.error('Failed to fetch audit log:', err);
      setAuditLogs([]);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleResetPassword = (userId, userName) => {
    alert(`Reset password functionality coming soon for ${userName}!`);
  };

  const openCreateModal = () => {
    setFormData({
      email: '',
      password: '',
      full_name: '',
      credentials: '',
      role: 'pharmacist',
      default_pay_rate: 0,
      default_pay_method: 'per_medication',
    });
    setShowCreateModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    const payRateDollars = (user.default_pay_rate || 0) / 100;
    setFormData({
      full_name: user.full_name,
      credentials: user.credentials || '',
      email: user.email,
      role: user.role || 'pharmacist',
      is_active: user.status === 'active',
      default_pay_rate: payRateDollars,
      default_pay_method: user.default_pay_method || 'per_medication',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setDeletingUser(user);
    setShowDeleteModal(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
    if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    return formatDate(dateStr);
  };

  const formatAuditDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAuditEventLabel = (eventType) => {
    const labels = {
      created: 'Created',
      updated: 'Updated',
      deleted: 'Deleted',
      activated: 'Activated',
      deactivated: 'Deactivated',
      pay_config_changed: 'Pay Config Changed',
      login_success: 'Login Success',
      login_failure: 'Login Failed',
      session_expired: 'Session Expired',
    };
    return labels[eventType] || eventType;
  };

  const getAuditEventColor = (eventType) => {
    const colors = {
      created: '#28a745',
      updated: '#17a2b8',
      deleted: '#dc3545',
      activated: '#28a745',
      deactivated: '#ffc107',
      pay_config_changed: '#6c757d',
      login_success: '#28a745',
      login_failure: '#dc3545',
      session_expired: '#6c757d',
    };
    return colors[eventType] || '#17a2b8';
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="admin-user-management">
      <div className="users-header">
        <h3>User Management</h3>
        <button type="button" className="btn-primary" onClick={openCreateModal}>
          + Create New User
        </button>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Credentials</th>
              <th>Status</th>
              <th>Last Active</th>
              <th>Registered</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  No users found. Create one to get started!
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id} className={user.status === 'inactive' ? 'inactive-row' : ''}>
                  <td className="user-name">{user.full_name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>{user.credentials || '-'}</td>
                  <td>
                    <span className={`status-badge status-${user.status || 'active'}`}>
                      {user.status || 'active'}
                    </span>
                  </td>
                  <td>{formatRelativeTime(user.last_active_at)}</td>
                  <td>{formatDate(user.created_at)}</td>
                  <td className="actions-cell">
                    <button
                      type="button"
                      className="btn-small btn-secondary"
                      onClick={() => openEditModal(user)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`btn-small ${user.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => handleToggleStatus(user.id, user.status || 'active', user.full_name)}
                    >
                      {user.status === 'active' ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      type="button"
                      className="btn-small btn-danger"
                      onClick={() => openDeleteModal(user)}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      className="btn-small btn-secondary"
                      onClick={() => handleViewAudit(user)}
                    >
                      History
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New User</h3>
            <form onSubmit={handleCreateUser}>
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="pharmacist@example.com"
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  minLength="8"
                />
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  placeholder="Dr. Jane Smith"
                />
              </div>

              <div className="form-group">
                <label>Credentials</label>
                <input
                  type="text"
                  value={formData.credentials}
                  onChange={(e) => setFormData({...formData, credentials: e.target.value})}
                  placeholder="PharmD, RPh"
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="pharmacist">Pharmacist</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Default Pay Rate ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_pay_rate}
                  onChange={(e) => setFormData({...formData, default_pay_rate: e.target.value})}
                  placeholder="25.00"
                />
                <small>Rate per medication or hourly rate</small>
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="payment_method"
                      value="per_medication"
                      checked={formData.default_pay_method === 'per_medication'}
                      onChange={(e) => setFormData({...formData, default_pay_method: e.target.value})}
                    />
                    <span>Per Medication</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="payment_method"
                      value="per_hour"
                      checked={formData.default_pay_method === 'per_hour'}
                      onChange={(e) => setFormData({...formData, default_pay_method: e.target.value})}
                    />
                    <span>Per Hour</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="payment_method"
                      value="other"
                      checked={formData.default_pay_method === 'other'}
                      onChange={(e) => setFormData({...formData, default_pay_method: e.target.value})}
                    />
                    <span>Other</span>
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && editingUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Edit User: {editingUser.full_name}</h3>
            <form onSubmit={handleEditUser}>
              <div className="form-group">
                <label>Email (read-only)</label>
                <input
                  type="email"
                  value={formData.email}
                  disabled
                  className="input-disabled"
                />
              </div>

              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Credentials</label>
                <input
                  type="text"
                  value={formData.credentials}
                  onChange={(e) => setFormData({...formData, credentials: e.target.value})}
                  placeholder="PharmD, RPh"
                />
              </div>

              <div className="form-group">
                <label>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="pharmacist">Pharmacist</option>
                  <option value="admin">Admin</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <div className="toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Default Pay Rate ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.default_pay_rate}
                  onChange={(e) => setFormData({...formData, default_pay_rate: e.target.value})}
                  placeholder="25.00"
                />
                <small>Rate per medication or hourly rate</small>
              </div>

              <div className="form-group">
                <label>Payment Method</label>
                <select
                  value={formData.default_pay_method}
                  onChange={(e) => setFormData({...formData, default_pay_method: e.target.value})}
                >
                  <option value="per_medication">Per Medication</option>
                  <option value="per_hour">Per Hour</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && deletingUser && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Delete User: {deletingUser.full_name}</h3>
            <p className="confirm-message">
              This will deactivate <strong>{deletingUser.full_name}</strong> and release their pending reviews. Are you sure?
            </p>
            <p className="confirm-warning">This action cannot be undone, but the user record will be preserved for audit purposes.</p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button type="button" className="btn-danger" onClick={handleDeleteUser}>
                Yes, Deactivate User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Audit History Drawer */}
      {showAuditDrawer && auditUser && (
        <div className="modal-overlay" onClick={() => setShowAuditDrawer(false)}>
          <div className="modal-content drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Activity History: {auditUser.full_name}</h3>
              <button type="button" className="btn-close" onClick={() => setShowAuditDrawer(false)}>×</button>
            </div>

            <div className="drawer-body">
              {loadingAudit ? (
                <div className="loading">Loading activity...</div>
              ) : auditLogs.length === 0 ? (
                <p className="no-data">No activity recorded yet.</p>
              ) : (
                <div className="audit-timeline">
                  {auditLogs.map(log => (
                    <div key={log.id} className="audit-entry">
                      <div className="audit-dot" style={{ background: getAuditEventColor(log.event_type) }} />
                      <div className="audit-content">
                        <div className="audit-header">
                          <span
                            className="audit-badge"
                            style={{ background: getAuditEventColor(log.event_type) }}
                          >
                            {getAuditEventLabel(log.event_type)}
                          </span>
                          <span className="audit-time">{formatAuditDate(log.created_at)}</span>
                        </div>
                        {log.changed_fields && (
                          <div className="audit-changes">
                            {Object.entries(log.changed_fields).map(([field, change]) => (
                              <div key={field} className="audit-change-row">
                                <span className="audit-field">{field}:</span>
                                <span className="audit-old">{String(change.old) || '(none)'}</span>
                                <span className="audit-arrow">→</span>
                                <span className="audit-new">{String(change.new) || '(none)'}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {log.metadata && (
                          <div className="audit-metadata">
                            {Object.entries(log.metadata).map(([key, val]) => (
                              <span key={key} className="meta-item">
                                {key}: {String(val)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
